import { useState, useCallback } from "react";
import { MYGEOTOKENS_WALLET_DISPLAY_NAME } from "@/constants/wallet-branding";

interface PhantomConnectionState {
	isConnecting: boolean;
	error: string | null;
	walletAddress: string | null;
}

interface PhantomConnectionReturn extends PhantomConnectionState {
	detectPhantom: () => boolean;
	connectPhantom: () => Promise<string>;
	resetConnection: () => void;
}

/**
 * Hook for handling Phantom wallet detection and connection
 * Provides reliable Phantom extension detection and connection with proper error handling
 */
export const usePhantomConnection = (): PhantomConnectionReturn => {
	const [state, setState] = useState<PhantomConnectionState>({
		isConnecting: false,
		error: null,
		walletAddress: null,
	});

	/**
	 * Detect if Phantom extension is available
	 * @returns boolean indicating if Phantom is available
	 */
	const detectPhantom = useCallback((): boolean => {
		try {
			// Check if we're in browser environment
			if (typeof window === "undefined") {
				return false;
			}

			const solana = (window as any).solana;
			const isPhantomAvailable = !!(solana && solana.isPhantom);
			return isPhantomAvailable;
		} catch (error) {
			console.error(" Error detecting Phantom:", error);
			return false;
		}
	}, []);

	/**
	 * Handle different types of Phantom connection errors
	 * @param error - The error object from Phantom connection
	 * @returns Formatted error message for user display
	 */
	const handlePhantomErrors = useCallback((error: any): string => {
		console.error(" Phantom connection error details:", error);

		// User rejection errors
		if (
			error.code === 4001 ||
			error.message?.includes("rejected") ||
			error.message?.includes("denied") ||
			error.message?.includes("cancelled") ||
			error.message?.includes("User rejected")
		) {
			return `Connection was rejected. Please approve the connection in ${MYGEOTOKENS_WALLET_DISPLAY_NAME} to continue.`;
		}

		// Extension not found errors
		if (
			error.message?.includes("not found") ||
			error.message?.includes("not detected") ||
			error.message?.includes("not installed")
		) {
			return `${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found. Please install it and refresh the page.`;
		}

		// Network/timeout errors
		if (
			error.message?.includes("timeout") ||
			error.message?.includes("network") ||
			error.code === -32603
		) {
			return "Connection timeout. Please try again or check your internet connection.";
		}

		// Already connected errors
		if (error.message?.includes("already connected")) {
			return `${MYGEOTOKENS_WALLET_DISPLAY_NAME} is already connected. Please disconnect and try again.`;
		}

		// Extension locked errors
		if (
			error.message?.includes("locked") ||
			error.message?.includes("unlock")
		) {
			return `${MYGEOTOKENS_WALLET_DISPLAY_NAME} is locked. Please unlock your browser extension and try again.`;
		}

		// Generic fallback
		if (error.message) {
			return `Connection failed: ${error.message}`;
		}

		return `Failed to connect to ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`;
	}, []);

	/**
	 * Connect to Phantom wallet and get wallet address
	 * @returns Promise<string> - The wallet address
	 * @throws Error if connection fails
	 */
	const connectPhantom = useCallback(async (): Promise<string> => {
		// Reset previous errors
		setState((prev) => ({ ...prev, error: null, isConnecting: true }));

		try {
			// Check if Phantom is available
			if (!detectPhantom()) {
				throw new Error(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not detected. Please install it and refresh the page.`
				);
			}

			const solana = (window as any).solana;

			// Double-check Phantom object
			if (!solana || !solana.isPhantom) {
				throw new Error(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found or not properly loaded.`
				);
			}
			// Request connection with timeout
			const connectionPromise = solana.connect();
			const timeoutPromise = new Promise((_, reject) => {
				setTimeout(
					() => reject(new Error("Connection timeout after 30 seconds")),
					30000
				);
			});

			const response = await Promise.race([connectionPromise, timeoutPromise]);

			if (!response || !response.publicKey) {
				throw new Error(
					`Invalid response from ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`
				);
			}

			const walletAddress = response.publicKey.toString();
			// Update state with success
			setState((prev) => ({
				...prev,
				isConnecting: false,
				walletAddress,
				error: null,
			}));

			return walletAddress;
		} catch (error: any) {
			const errorMessage = handlePhantomErrors(error);

			// Update state with error
			setState((prev) => ({
				...prev,
				isConnecting: false,
				error: errorMessage,
				walletAddress: null,
			}));

			// Re-throw with formatted message for caller
			throw new Error(errorMessage);
		}
	}, [detectPhantom, handlePhantomErrors]);

	/**
	 * Reset connection state
	 */
	const resetConnection = useCallback(() => {
		setState({
			isConnecting: false,
			error: null,
			walletAddress: null,
		});
	}, []);

	return {
		...state,
		detectPhantom,
		connectPhantom,
		resetConnection,
	};
};
